import React, { useEffect, useRef, useState } from 'react';
import './Spaceship.css';

const SpaceshipGame = () => {
  const canvasRef = useRef(null);
  const gameOverRef = useRef(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [sprites, setSprites] = useState({
    player: null,
    enemy1: null,
    enemy2: null,
    enemy3: null,
    loaded: false
  });
  const [stars, setStars] = useState([]);
  
  // Preload sprite images
  useEffect(() => {
    const loadSprites = async () => {
      try {
        console.log('Loading sprite images...');
        
        // Create image objects
        const playerImg = new Image();
        const enemy1Img = new Image();
        const enemy2Img = new Image();
        const enemy3Img = new Image();
        
        // Set source paths using PNG files from public folder
        playerImg.src = `${process.env.PUBLIC_URL}/sprites/playerSprite.png`;
        enemy1Img.src = `${process.env.PUBLIC_URL}/sprites/enemySprite1.png`;
        enemy2Img.src = `${process.env.PUBLIC_URL}/sprites/enemySprite2.png`;
        enemy3Img.src = `${process.env.PUBLIC_URL}/sprites/enemySprite3.png`;
        
        console.log('Player sprite URL:', playerImg.src);
        
        // Wait for all images to load or handle errors
        const handleImageLoad = (img) => {
          return new Promise((resolve) => {
            img.onload = () => {
              console.log(`Loaded: ${img.src}`);
              resolve(img);
            };
            img.onerror = () => {
              console.warn(`Failed to load sprite: ${img.src}`);
              resolve(null); // Resolve with null if image fails to load
            };
          });
        };
        
        // Wait for all images to load
        const [loadedPlayer, loadedEnemy1, loadedEnemy2, loadedEnemy3] = await Promise.all([
          handleImageLoad(playerImg),
          handleImageLoad(enemy1Img),
          handleImageLoad(enemy2Img),
          handleImageLoad(enemy3Img)
        ]);
        
        // Log loading results
        console.log('Sprites loaded:', {
          player: !!loadedPlayer,
          enemy1: !!loadedEnemy1,
          enemy2: !!loadedEnemy2,
          enemy3: !!loadedEnemy3
        });
        
        // Update sprites state
        setSprites({
          player: loadedPlayer,
          enemy1: loadedEnemy1,
          enemy2: loadedEnemy2,
          enemy3: loadedEnemy3,
          loaded: true
        });
      } catch (error) {
        console.error('Error loading sprite images:', error);
      }
    };
    
    loadSprites();
  }, []);
  
  // Draw start screen
  const drawStartScreen = () => {
    const canvas = canvasRef.current;
    if (!canvas) return; // Safety check
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas to full window
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Clear canvas
    ctx.fillStyle = 'black';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw stars background
    drawStars(ctx, canvas.width, canvas.height);
    
    // Draw title
    ctx.fillStyle = 'white';
    ctx.shadowColor = '#0066ff';
    ctx.shadowBlur = 15;
    ctx.font = '80px VT323, monospace';
    ctx.textAlign = 'center';
    ctx.fillText('SPACE INVADERS', canvas.width / 2, canvas.height / 4);
    
    // Draw BBGlobal messages
    ctx.shadowBlur = 10;
    ctx.font = '40px VT323, monospace';
    ctx.fillStyle = '#00ff00';
    ctx.fillText('BBGlobal Solutions', canvas.width / 2, canvas.height / 4 + 60);
    
    ctx.font = '30px VT323, monospace';
    ctx.fillStyle = '#00ffff';
    ctx.fillText('Transforming Businesses with AI', canvas.width / 2, canvas.height / 4 + 100);
    
    ctx.font = '25px VT323, monospace';
    ctx.fillStyle = '#ff00ff';
    ctx.fillText('Join the AI Revolution', canvas.width / 2, canvas.height / 4 + 140);
    
    // Draw instructions
    ctx.shadowBlur = 0;
    ctx.font = '30px VT323, monospace';
    ctx.fillStyle = 'white';
    ctx.fillText('PRESS ENTER TO START', canvas.width / 2, canvas.height / 2 - 40);
    
    // Draw controls
    ctx.font = '25px VT323, monospace';
    const lineHeight = 35;
    let lineY = canvas.height / 2 + 20;
    
    ctx.fillText('CONTROLS:', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('ARROW KEYS / WASD: MOVE SHIP', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('SPACE: FIRE', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('R: RESTART WHEN GAME OVER', canvas.width / 2, lineY);
    
    // Draw features
    lineY += lineHeight * 1.5;
    ctx.fillText('FEATURES:', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('MULTIPLE ENEMY TYPES WITH DIFFERENT WEAPONS', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('PROGRESSING DIFFICULTY WITH EACH LEVEL', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('ENEMIES WILL TURN BACK BEFORE REACHING BOTTOM', canvas.width / 2, lineY);
    lineY += lineHeight;
    ctx.fillText('FULL 360° PLAYER SHIP MOVEMENT', canvas.width / 2, lineY);
  };
  
  // Draw stars for background
  const drawStars = (ctx, width, height) => {
    // If stars are already generated, just draw them
    if (stars.length > 0) {
      ctx.fillStyle = 'white';
      for (const star of stars) {
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();
      }
      return;
    }
    
    // Generate new stars if they don't exist yet
    const newStars = [];
    ctx.fillStyle = 'white';
    for (let i = 0; i < 200; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const size = Math.random() * 2;
      newStars.push({ x, y, size });
      
      ctx.beginPath();
      ctx.arc(x, y, size, 0, Math.PI * 2);
      ctx.fill();
    }
    setStars(newStars); // Store the generated stars
  };
  
  // Component-level event listener for keys
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'r' && gameOverRef.current) {
        window.location.reload();
      }
      
      // Handle Enter key to start game
      if (event.key === 'Enter' && !gameStarted) {
        setGameStarted(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [gameStarted]);
  
  // Draw the start screen
  useEffect(() => {
    if (!gameStarted) {
      drawStartScreen();
      
      // Redraw start screen on window resize
      const handleResize = () => {
        drawStartScreen();
      };
      
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [gameStarted]);
  
  // Main game logic
  useEffect(() => {
    if (!gameStarted) return; // Don't initialize game until started
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas to full window
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    // Game state
    let score = 0;
    let lives = 3;
    let level = 1;
    let gameOver = false;
    let levelCompleted = false;
    let levelCompletedTime = 0;
    let bullets = [];
    let enemyBullets = [];
    let enemies = [];
    
    // Keep gameOver state in ref for access outside this effect
    gameOverRef.current = gameOver;
    
    // Player ship properties
    const player = {
      x: canvas.width / 2,
      y: canvas.height - 100,
      width: 50,
      height: 30,
      speed: 8,
      color: '#00ff00',
      lastFireTime: 0
    };
    
    // Game settings
    const FIRE_RATE = 300; // Time between shots when space is held (milliseconds)
    const BULLET_SPEED = 10;
    const ENEMY_BULLET_SPEED = 5;
    const ENEMY_ROWS_BASE = 1; // Base number of rows for level 1
    const ENEMY_COLS_BASE = 3; // Base number of columns for level 1
    const ENEMY_WIDTH = 40;
    const ENEMY_HEIGHT = 30;
    const ENEMY_PADDING = 20;
    const ENEMY_TOP_OFFSET = 100;
    const ENEMY_SPEED = 2;
    const ENEMY_MAX_Y = canvas.height - 200; // Maximum Y position before reversing
    const ENEMY_FIRE_CHANCE = 0.005; // Chance for an enemy to fire per frame
    
    // Keyboard handling
    const keys = {
      ArrowLeft: false,
      ArrowRight: false,
      ArrowUp: false,    // Add up movement
      ArrowDown: false,  // Add down movement
      'w': false,        // WASD movement
      'a': false,
      's': false,
      'd': false,
      ' ': false         // Space key
    };
    
    const handleKeyDown = (e) => {
      if (e.key in keys) {
        keys[e.key] = true;
      }
    };
    
    const handleKeyUp = (e) => {
      if (e.key in keys) {
        keys[e.key] = false;
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    
    // Create enemies in formation based on current level
    const createEnemies = () => {
      enemies = [];
      
      // Increase number of enemies with level
      const rows = Math.min(ENEMY_ROWS_BASE + Math.floor(level / 2), 5); // Cap at 5 rows
      const cols = Math.min(ENEMY_COLS_BASE + level - 1, 10); // Cap at 10 columns
      
      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          // Determine enemy type based on level and position
          // Mix up enemy types more in higher levels
          let enemyType;
          if (level <= 2) {
            enemyType = row % 3; // Simple pattern for early levels
          } else if (level <= 5) {
            enemyType = (row + col) % 3; // Checkerboard pattern
          } else {
            enemyType = Math.floor(Math.random() * 3); // Random for higher levels
          }
          
          enemies.push({
            x: col * (ENEMY_WIDTH + ENEMY_PADDING) + ENEMY_PADDING,
            y: row * (ENEMY_HEIGHT + ENEMY_PADDING) + ENEMY_TOP_OFFSET,
            width: ENEMY_WIDTH,
            height: ENEMY_HEIGHT,
            alive: true,
            row: row,
            type: enemyType, // 0, 1, or 2 for different types
            direction: 1, // Movement direction (1 = right, -1 = left)
            color: enemyType === 0 ? '#ff0000' : enemyType === 1 ? '#ff6600' : '#ffcc00',
            points: (rows - row) * 10 * (1 + Math.floor((level - 1) / 3)) // More points in higher levels
          });
        }
      }
    };
    
    // Fire bullet from player
    const fireBullet = () => {
      bullets.push({
        x: player.x + player.width / 2 - 3, // Center the bullet
        y: player.y,
        width: 6, // Make bullet wider
        height: 18, // Make bullet longer
        color: '#00ff00' // Change color to green
      });
    };
    
    // Fire bullet from enemy with different bullet types
    const fireEnemyBullet = (enemy) => {
      // Different bullet types per enemy type
      const bulletTypes = [
        { width: 6, height: 12, color: '#ff0000', speed: ENEMY_BULLET_SPEED }, // Type 0: Red bullets
        { width: 8, height: 8, color: '#ff8800', speed: ENEMY_BULLET_SPEED * 1.3 }, // Type 1: Orange square bullets (faster)
        { width: 5, height: 15, color: '#ffff00', speed: ENEMY_BULLET_SPEED * 0.8 } // Type 2: Yellow bullets (slower)
      ];
      
      const bulletType = bulletTypes[enemy.type];
      
      enemyBullets.push({
        x: enemy.x + enemy.width / 2 - bulletType.width / 2,
        y: enemy.y + enemy.height,
        width: bulletType.width,
        height: bulletType.height,
        color: bulletType.color,
        speed: bulletType.speed,
        type: enemy.type
      });
    };
    
    // Update player position and handle shooting
    const updatePlayer = () => {
      // Move player left/right/up/down with boundaries
      if ((keys.ArrowLeft || keys.a) && player.x > 0) {
        player.x -= player.speed;
      }
      if ((keys.ArrowRight || keys.d) && player.x < canvas.width - player.width) {
        player.x += player.speed;
      }
      // Add up/down movement with boundaries
      if ((keys.ArrowUp || keys.w) && player.y > 0) {
        player.y -= player.speed;
      }
      if ((keys.ArrowDown || keys.s) && player.y < canvas.height - player.height) {
        player.y += player.speed;
      }
      
      // Fire bullet
      if (keys[' ']) {
        const currentTime = Date.now();
        if (currentTime - player.lastFireTime > FIRE_RATE) {
          fireBullet();
          player.lastFireTime = currentTime;
        }
      }
    };
    
    // Check for level completion
    const checkLevelCompletion = () => {
      if (enemies.every(e => !e.alive) && !levelCompleted) {
        levelCompleted = true;
        levelCompletedTime = Date.now();
        setTimeout(() => {
          level++;
          levelCompleted = false;
          createEnemies();
        }, 3000); // Wait 3 seconds before starting next level
      }
    };
    
    // Update bullets with different speeds and behaviors
    const updateBullets = () => {
      // Update player bullets
      for (let i = bullets.length - 1; i >= 0; i--) {
        bullets[i].y -= BULLET_SPEED;
        
        // Remove bullets that are off screen
        if (bullets[i].y < 0) {
          bullets.splice(i, 1);
          continue;
        }
        
        // Check for collisions with enemies
        for (let j = 0; j < enemies.length; j++) {
          const enemy = enemies[j];
          if (enemy.alive && 
              bullets[i].x < enemy.x + enemy.width &&
              bullets[i].x + bullets[i].width > enemy.x &&
              bullets[i].y < enemy.y + enemy.height &&
              bullets[i].y + bullets[i].height > enemy.y) {
            
            // Hit an enemy
            enemy.alive = false;
            bullets.splice(i, 1);
            score += enemy.points;
            
            // Check if all enemies are dead
            checkLevelCompletion();
            
            break;
          }
        }
      }
      
      // Update enemy bullets with different speeds
      for (let i = enemyBullets.length - 1; i >= 0; i--) {
        enemyBullets[i].y += enemyBullets[i].speed;
        
        // Type 1 bullets also move horizontally (wavy pattern)
        if (enemyBullets[i].type === 1) {
          enemyBullets[i].x += Math.sin(enemyBullets[i].y * 0.05) * 2;
        }
        
        // Remove bullets that are off screen
        if (enemyBullets[i].y > canvas.height) {
          enemyBullets.splice(i, 1);
          continue;
        }
        
        // Check for collision with player
        if (enemyBullets[i].x < player.x + player.width &&
            enemyBullets[i].x + enemyBullets[i].width > player.x &&
            enemyBullets[i].y < player.y + player.height &&
            enemyBullets[i].y + enemyBullets[i].height > player.y) {
          
          // Player hit
          lives--;
          enemyBullets.splice(i, 1);
          
          if (lives <= 0) {
            gameOver = true;
            gameOverRef.current = true;
          }
          
          break;
        }
      }
    };
    
    // Update enemies
    const updateEnemies = () => {
      let anyEnemyReachedEdge = false;
      let directionToChange = 0;
      
      // Check if any enemies hit the edge of the screen
      for (let i = 0; i < enemies.length; i++) {
        const enemy = enemies[i];
        if (!enemy.alive) continue;
        
        if (enemy.x <= 0 && enemy.direction < 0) {
          anyEnemyReachedEdge = true;
          directionToChange = 1; // Change to right
          break;
        } else if (enemy.x + enemy.width >= canvas.width && enemy.direction > 0) {
          anyEnemyReachedEdge = true;
          directionToChange = -1; // Change to left
          break;
        }
      }
      
      // Set of enemies that have reached max Y
      const reachedMaxY = new Set();
      
      // Move enemies
      for (let i = 0; i < enemies.length; i++) {
        const enemy = enemies[i];
        if (!enemy.alive) continue;
        
        // If any enemy reached the edge, change direction and move down
        if (anyEnemyReachedEdge) {
          enemy.direction = directionToChange;
          enemy.y += ENEMY_HEIGHT / 2;
        }
        
        // Move horizontally
        enemy.x += ENEMY_SPEED * enemy.direction;
        
        // Check if enemies reached the maximum Y position
        if (enemy.y + enemy.height >= ENEMY_MAX_Y) {
          reachedMaxY.add(i);
        }
        
        // Random chance for enemy to fire based on type
        // Type 0 fires less frequently, Type 2 fires more frequently
        const fireChances = [ENEMY_FIRE_CHANCE * 0.7, ENEMY_FIRE_CHANCE, ENEMY_FIRE_CHANCE * 1.5];
        if (Math.random() < fireChances[enemy.type]) {
          fireEnemyBullet(enemy);
        }
      }
      
      // Reverse direction of enemies that have reached max Y
      for (const i of reachedMaxY) {
        const enemy = enemies[i];
        if (!enemy.alive) continue;
        
        // Move back up
        enemy.y = ENEMY_MAX_Y - enemy.height;
        // Sometimes change horizontal direction too
        if (Math.random() < 0.3) {
          enemy.direction *= -1;
        }
      }
    };
    
    // Draw the player
    const drawPlayer = () => {
      try {
        if (sprites.loaded && sprites.player) {
          // Draw the player ship sprite
          ctx.drawImage(sprites.player, player.x, player.y, player.width, player.height);
        } else {
          // Fallback to drawing a shape if image isn't loaded
          drawPlayerFallback();
        }
      } catch (error) {
        console.error('Error drawing player:', error);
        // Fallback to drawing a simple shape
        drawPlayerFallback();
      }
    };
    
    // Draw player ship fallback
    const drawPlayerFallback = () => {
      // Draw player triangle ship
      ctx.fillStyle = player.color;
      ctx.beginPath();
      ctx.moveTo(player.x + player.width / 2, player.y);
      ctx.lineTo(player.x + player.width, player.y + player.height);
      ctx.lineTo(player.x, player.y + player.height);
      ctx.closePath();
      ctx.fill();
    };
    
    // Draw enemies (with sprites properly oriented)
    const drawEnemies = () => {
      for (let i = 0; i < enemies.length; i++) {
        const enemy = enemies[i];
        if (!enemy.alive) continue;
        
        try {
          // Determine which sprite to use based on enemy type
          let enemySprite = null;
          if (sprites.loaded) {
            if (enemy.type === 0 && sprites.enemy1) {
              enemySprite = sprites.enemy1;
            } else if (enemy.type === 1 && sprites.enemy2) {
              enemySprite = sprites.enemy2;
            } else if (sprites.enemy3) {
              enemySprite = sprites.enemy3;
            }
          }
          
          if (enemySprite) {
            // Save context for transformation
            ctx.save();
            
            // Translate to the center of the enemy
            ctx.translate(enemy.x + enemy.width / 2, enemy.y + enemy.height / 2);
            
            // Rotate 180 degrees to make sprites face downward
            ctx.rotate(Math.PI);
            
            // Draw the enemy sprite centered at origin
            ctx.drawImage(
              enemySprite, 
              -enemy.width / 2, 
              -enemy.height / 2, 
              enemy.width, 
              enemy.height
            );
            
            // Restore context
            ctx.restore();
          } else {
            // Fallback to colored shapes if sprites aren't loaded
            drawEnemyFallback(enemy);
          }
        } catch (error) {
          console.error('Error drawing enemy:', error);
          // Fallback to simple shape
          drawEnemyFallback(enemy);
        }
      }
    };
    
    // Draw enemy fallback (also rotated)
    const drawEnemyFallback = (enemy) => {
      ctx.fillStyle = enemy.color;
      
      // Save context for transformation
      ctx.save();
      
      // Translate to the center of the enemy
      ctx.translate(enemy.x + enemy.width / 2, enemy.y + enemy.height / 2);
      
      // Rotate 180 degrees to match sprite orientation
      ctx.rotate(Math.PI);
      
      if (enemy.type === 0) {
        // Draw a circle for type 0 enemies
        ctx.beginPath();
        ctx.arc(0, 0, Math.min(enemy.width, enemy.height) / 2, 0, Math.PI * 2);
        ctx.fill();
      } else if (enemy.type === 1) {
        // Draw a diamond for type 1 enemies
        ctx.beginPath();
        ctx.moveTo(0, -enemy.height / 2);
        ctx.lineTo(enemy.width / 2, 0);
        ctx.lineTo(0, enemy.height / 2);
        ctx.lineTo(-enemy.width / 2, 0);
        ctx.closePath();
        ctx.fill();
      } else {
        // Draw a square for type 2 enemies
        ctx.fillRect(-enemy.width / 2, -enemy.height / 2, enemy.width, enemy.height);
      }
      
      // Restore context
      ctx.restore();
    };
    
    // Draw bullets with different colors and shapes
    const drawBullets = () => {
      // Draw player bullets
      ctx.fillStyle = '#00ff00'; // Green bullets for player
      for (let i = 0; i < bullets.length; i++) {
        ctx.fillRect(bullets[i].x, bullets[i].y, bullets[i].width, bullets[i].height);
      }
      
      // Draw enemy bullets with different shapes based on type
      for (let i = 0; i < enemyBullets.length; i++) {
        const bullet = enemyBullets[i];
        ctx.fillStyle = bullet.color;
        
        if (bullet.type === 0) {
          // Type 0: Simple rectangle
          ctx.fillRect(bullet.x, bullet.y, bullet.width, bullet.height);
        } else if (bullet.type === 1) {
          // Type 1: Diamond shape
          ctx.beginPath();
          ctx.moveTo(bullet.x + bullet.width / 2, bullet.y);
          ctx.lineTo(bullet.x + bullet.width, bullet.y + bullet.height / 2);
          ctx.lineTo(bullet.x + bullet.width / 2, bullet.y + bullet.height);
          ctx.lineTo(bullet.x, bullet.y + bullet.height / 2);
          ctx.closePath();
          ctx.fill();
        } else {
          // Type 2: Circle
          const radius = Math.min(bullet.width, bullet.height) / 2;
          ctx.beginPath();
          ctx.arc(
            bullet.x + bullet.width / 2,
            bullet.y + bullet.height / 2,
            radius,
            0,
            Math.PI * 2
          );
          ctx.fill();
        }
      }
    };
    
    // Draw level completed message
    const drawLevelCompleted = () => {
      // Semi-transparent overlay
      ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Level completed text with glow effect
      ctx.fillStyle = 'white';
      ctx.shadowColor = '#00ff00';
      ctx.shadowBlur = 15;
      ctx.font = '60px VT323, monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`LEVEL ${level} COMPLETED!`, canvas.width / 2, canvas.height / 2 - 40);
      
      // Score and next level info
      ctx.shadowBlur = 0;
      ctx.font = '30px VT323, monospace';
      ctx.fillText(`SCORE: ${score}`, canvas.width / 2, canvas.height / 2 + 20);
      
      // Countdown to next level
      const remainingTime = Math.ceil((levelCompletedTime + 3000 - Date.now()) / 1000);
      ctx.fillText(`NEXT LEVEL IN ${remainingTime}...`, canvas.width / 2, canvas.height / 2 + 60);
    };
    
    // Draw score and lives
    const drawHUD = () => {
      ctx.fillStyle = 'white';
      ctx.font = '20px VT323, monospace';
      ctx.textAlign = 'left';
      ctx.fillText(`SCORE: ${score}`, 20, 30);
      ctx.fillText(`LIVES: ${lives}`, 20, 60);
      ctx.fillText(`LEVEL: ${level}`, 20, 90);
      
      // Draw enemy count
      const aliveEnemies = enemies.filter(e => e.alive).length;
      ctx.fillText(`ENEMIES: ${aliveEnemies}/${enemies.length}`, 20, 120);
      
      // Level info
      const levelDescription = level <= 2 ? 'EASY' : level <= 5 ? 'MEDIUM' : 'HARD';
      ctx.fillText(`DIFFICULTY: ${levelDescription}`, canvas.width - 200, 30);
      
      // Weapon info
      ctx.fillText('WEAPON: STANDARD LASER', canvas.width - 200, 60);
    };
    
    // Draw game over screen
    const drawGameOver = () => {
      // Semi-transparent background
      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Game over text
      ctx.fillStyle = 'white';
      ctx.shadowColor = 'red';
      ctx.shadowBlur = 15;
      ctx.font = '70px VT323, monospace';
      ctx.textAlign = 'center';
      ctx.fillText('GAME OVER', canvas.width / 2, canvas.height / 2 - 50);
      
      // Score and restart instructions
      ctx.shadowBlur = 0;
      ctx.font = '35px VT323, monospace';
      ctx.fillText(`FINAL SCORE: ${score}`, canvas.width / 2, canvas.height / 2 + 10);
      ctx.fillText('PRESS R TO RESTART', canvas.width / 2, canvas.height / 2 + 60);
      
      // Restart button
      const buttonX = canvas.width / 2;
      const buttonY = canvas.height / 2 + 120;
      const buttonWidth = 200;
      const buttonHeight = 50;
      
      ctx.fillStyle = '#4CAF50';
      ctx.fillRect(buttonX - buttonWidth/2, buttonY - buttonHeight/2, buttonWidth, buttonHeight);
      
      ctx.fillStyle = 'white';
      ctx.font = '25px VT323, monospace';
      ctx.fillText('RESTART GAME', buttonX, buttonY + 8);
      
      // Add click handler for the button
      canvas.onclick = (e) => {
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        
        if (
          mouseX >= buttonX - buttonWidth/2 &&
          mouseX <= buttonX + buttonWidth/2 &&
          mouseY >= buttonY - buttonHeight/2 &&
          mouseY <= buttonY + buttonHeight/2
        ) {
          window.location.reload();
        }
      };
      
      // Add BBGlobal messages
      ctx.font = '40px VT323, monospace';
      ctx.fillStyle = '#00ff00';
      ctx.fillText('BBGlobal Solutions', canvas.width / 2, canvas.height / 2 + 100);
      
      ctx.font = '30px VT323, monospace';
      ctx.fillStyle = '#00ffff';
      ctx.fillText('Empowering Businesses with AI', canvas.width / 2, canvas.height / 2 + 150);
      
      ctx.font = '25px VT323, monospace';
      ctx.fillStyle = '#ff00ff';
      ctx.fillText('Visit BBglobalsolutions.org', canvas.width / 2, canvas.height / 2 + 190);
      
      ctx.font = '20px VT323, monospace';
      ctx.fillStyle = '#ffff00';
      ctx.fillText('The Future of Business is Here', canvas.width / 2, canvas.height / 2 + 230);
    };
    
    // Game loop
    const gameLoop = () => {
      // Clear canvas
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw stars background
      drawStars(ctx, canvas.width, canvas.height);
      
      if (!gameOver) {
        if (!levelCompleted) {
          updatePlayer();
          updateBullets();
          updateEnemies();
        }
        
        drawPlayer();
        drawEnemies();
        drawBullets();
        
        if (levelCompleted) {
          drawLevelCompleted();
        }
      }
      
      drawHUD();
      
      if (gameOver) {
        drawGameOver();
      }
      
      requestAnimationFrame(gameLoop);
    };
    
    // Initialize game
    const init = () => {
      createEnemies();
      gameLoop();
    };
    
    // Start game
    init();
    
    // Handle window resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Adjust player position
      player.y = canvas.height - 100;
    };
    
    window.addEventListener('resize', handleResize);
    
    // Cleanup event listeners on unmount
    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [gameStarted]);
  
  return (
    <div className="spaceship-container">
      <canvas ref={canvasRef} className="spaceship-canvas"></canvas>
    </div>
  );
};

export default SpaceshipGame; 